import { defineStore } from "pinia";
import axios from "axios";
import { ProjectStoreState, ProjectStoreActions } from "../types/ProjectStoreState";

export const useProjectStore = defineStore<
    "project",
    ProjectStoreState,
    {}, // no getters yet
    ProjectStoreActions
>("project", {
    state: () => ({
        project: {},
        projectMembers: [],
        priorities: [],
        taskStatuses: [],
        taskTags: [],
        tasks: [],
        projectRoles: [],
        employees: [],
        allEmployees: [],
        projectTags: [],
        projects: [],
        isLoading: false,
        queryData: {
            perPage: "all",
            taskStatus: "",
            status: 1,
        },
        projectManagementSubscription: null,
        projectSubscription: null,
        projectTabsList: [
            {
                name: "tOverView",
                icon: "solar:file-text-broken",
                iconWidth: 18,
                iconHeight: 18,
                label: "lang.labels.xin_overview",
                permission: "*",
            },
            {
                name: "tList",
                icon: "solar:clipboard-list-broken",
                iconWidth: 18,
                iconHeight: 18,
                label: "lang.labels.xin_role_list",
                permission: "*",
            },
            {
                name: "tBoard",
                icon: "fluent:board-28-regular",
                iconWidth: 20,
                iconHeight: 20,
                label: "lang.labels.board",
                permission: "*",
            },
            {
                name: "tClients",
                icon: "solar:user-broken",
                iconWidth: 18,
                iconHeight: 18,
                label: "lang.labels.project_credential",
                permission: "project-management.project-credentials",
            },
            {
                name: "tMedia",
                icon: "solar:gallery-wide-broken",
                iconWidth: 18,
                iconHeight: 18,
                label: "lang.labels.project_media",
                permission: "project-management.project.links-media",
            },
            {
                name: "tLogs",
                icon: "hugeicons:time-quarter-pass",
                iconWidth: 18,
                iconHeight: 18,
                label: "lang.labels.time_log",
                permission: "*",
            },
            {
                name: "tProfile",
                icon: "solar:user-id-broken",
                iconWidth: 20,
                iconHeight: 20,
                label: "lang.labels.work_profile",
                permission: "project-management.project.work-profile",
            },
            {
                name: "tDBilling",
                icon: "solar:bill-list-linear",
                iconWidth: 20,
                iconHeight: 20,
                label: "lang.labels.date_wise_billing",
                condition: "project.hourly_profile_count > 0",
                permission: "project-management.date-wise.billing",
            },
            {
                name: "tWLogs",
                icon: "solar:bill-list-linear",
                iconWidth: 20,
                iconHeight: 20,
                label: "lang.labels.weekly_billing_log",
                condition: "project.hourly_profile_count > 0",
                permission: "project-management.project.billing-report.weekly.log",
            },
            {
                name: "tChats",
                icon: "solar:chat-line-broken",
                iconWidth: 20,
                iconHeight: 20,
                label: "lang.labels.chat_project",
                permission: "*",
            },
        ],
        activeProjectTabs: {},
        projectPermissions: {}
    }),
    actions: {
        incrementTab(uuid: string) {
            const tabKey = `tabCounted_${uuid}`;
            if (!sessionStorage.getItem(tabKey)) {
                const tabs = JSON.parse(localStorage.getItem("activeProjectTabs") || "{}");
                tabs[uuid] = (tabs[uuid] || 0) + 1;
                localStorage.setItem("activeProjectTabs", JSON.stringify(tabs));
                this.activeProjectTabs = tabs;
                sessionStorage.setItem(tabKey, "true");
            }
        },
        decrementTab(uuid: string) {
            const tabs = JSON.parse(localStorage.getItem("activeProjectTabs") || "{}");
            if (tabs[uuid]) {
                tabs[uuid] -= 1;
                if (tabs[uuid] === 0) {
                    delete tabs[uuid];
                    if (this.project?.uuid === uuid) {
                        this.$reset();
                    }
                }
                localStorage.setItem("activeProjectTabs", JSON.stringify(tabs));
                this.activeProjectTabs = tabs;
            }
        },
        subscribeProjectEvent(uuid) {
            //unsubcribe
            if (this.projectSubscription) {
                window.Echo.leave(`project.${uuid}.updates`);
                this.projectSubscription = null;
            }
            this.projectSubscription = `project.${uuid}.updates`;
            window.Echo.channel(`project.${uuid}.updates`).listen(
                "\\Modules\\ProjectManagement\\Events\\ProjectUpdated",
                (e) => {
                    if (e.type === "tasks") {
                        // this.fetchProjectTasks(uuid, this.queryData);
                    } else if (e.type === "new-task") {
                        // this.fetchProjectTasks(uuid, this.queryData);
                    } else if (e.type === "media") {
                        // this.fetchProjectDetails(uuid);
                    } else if (e.type === "discussion") {
                        // this.fetchProjectDetails(uuid);
                    } else if (e.type === "members") {
                        // this.fetchProjectMembers(uuid);
                    }
                }
            );
        },
        subscribeProjectManagementEvent() {
            //unsubcribe
            if (this.projectManagementSubscription) {
                window.Echo.leave(`project.management`);
                this.projectManagementSubscription = null;
            }
            this.projectManagementSubscription = "project.management";
            window.Echo.channel(`project.management`).listen(
                "\\Modules\\ProjectManagement\\Events\\ProjectManagementUpdated",
                (e) => {
                    const taskData = e.data;
                    if (e.type === "tags") {
                        this.fetchProjectTags();
                    } else if (e.type === "priorities") {
                        if (taskData && taskData.length > 0) {
                            this.priorities = taskData;
                        }
                        // this.fetchPriorities();
                    } else if (e.type === "task-statuses") {
                        if (taskData && taskData.length > 0) {
                            this.taskStatuses = taskData;
                        }
                        // this.fetchTaskStatuses();
                    } else if (e.type === "task-tags") {
                        const taskTag = this.taskTags.find(
                            (tag) => tag.id === taskData.id
                        );
                        if (taskTag) {
                            Object.assign(taskTag, taskData);
                        } else {
                            this.taskTags.unshift(taskData);
                        }
                    }
                }
            );
        },
        async getProjectPermissions(uuid = null) {
            const p = this.projectPermissions?.[uuid] || Object.values(this.projectPermissions);
            if (p && p?.length > 0) {
                return;
            }
            try {
                const response = await axios.get(route("project-management.permissions.project"));
                this.projectPermissions = response.data.data;
            } catch (e) {
                console.log("Error on getting permissions", e)
            }
        },
        async fetchProjectDetails(uuid) {
            this.isLoading = true;
            try {
                const response = await axios.get(
                    route("project-management.show", {
                        uuid: uuid,
                    })
                );
                this.project = response.data.data;
            } catch (error) {
                console.error("Error fetching project details:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchPriorities() {
            try {
                if (this.priorities.length > 0) {
                    return;
                }
                const response = await axios.get(
                    route("project-management.priorities.index"),
                    {
                        params: this.queryData,
                    }
                );
                this.priorities = response.data.data;
            } catch (error) {
                console.error("Error fetching priorities:", error);
            }
        },
        async fetchTaskStatuses() {
            try {
                if (this.taskStatuses.length > 0) {
                    return;
                }
                const response = await axios.get(
                    route("project-management.task-statuses.index"),
                    {
                        params: this.queryData,
                    }
                );
                this.taskStatuses = response.data.data;
            } catch (error) {
                console.error("Error fetching task statuses:", error);
            }
        },
        async fetchTaskTags(id) {
            try {
                let query = {
                    ...this.queryData,
                    project_id: id || this.project?.id,
                };
                if (this.taskTags.length > 0) {
                    return;
                }
                const response = await axios.get(
                    route("project-management.task-tags.index"),
                    {
                        params: query,
                    }
                );
                this.taskTags = response.data.data;
            } catch (error) {
                console.error("Error fetching task tags:", error);
            }
        },
        async fetchProjectTasks(uuid, taskQuery, options = {}) {
            try {
                const response = await axios.get(
                    route("project-management.tasks.index", { uuid }),
                    {
                        params: taskQuery,
                        ...options,
                    }
                );

                this.tasks = response.data.data;
                return response; // 👈 return so caller can use pagination/meta
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log("Request canceled:", error.message);
                } else {
                    console.error("Error fetching tasks:", error);
                }
                throw error; // rethrow so caller can handle
            }
        },
        async fetchProjectMembers(uuid, projecVisibility = false) {
            try {
                const response = await axios.get(
                    route("project-management.projects-assign.index", {
                        uuid: uuid,
                    }),
                    {
                        params: {
                            perPage: "all",
                            status: "active",
                            project_visibility: projecVisibility ? 0 : 1,
                        },
                    }
                );
                this.projectMembers = response.data.data;
            } catch (error) {
                console.error("Error fetching project members:", error);
            }
        },
        async fetchProjectRoles() {
            try {
                let query = {
                    is_project_role: true,
                    withoutPermissions: true,
                    status: 1,
                };

                const response = await axios.get(route("hrm.roles.index"), {
                    params: query,
                });
                this.projectRoles = response.data.data;
            } catch (error) {
                console.error("Error fetching project roles:", error);
            }
        },
        async fetchEmployees(uuid) {
            let queryData = {
                perPage: "all",
                status: "active",
                pageType: "all_employee",
            };
            await axios
                .get(
                    route("project-management.assign.members", {
                        uuid: uuid,
                    }),
                    {
                        params: queryData,
                    }
                )
                .then((response) => {
                    this.employees = response.data.data;
                })
                .catch((error) => {
                    console.error("Error fetching employees:", error);
                })
                .finally(() => { });
        },
        async fetchProjectTags() {
            try {
                let query = {
                    resource_type:
                        "Modules\\ProjectManagement\\Entities\\Project",
                    perPage: "all",
                };

                const response = await axios.get(route("hrm.tags.index"), {
                    params: query,
                });
                this.projectTags = response.data.data;
            } catch (error) {
                console.error("Error fetching project tags:", error);
            }
        },
        async fetchProjects(localQuery) {
            try {
                localQuery = localQuery || {};
                let query = {
                    perPage: "all",
                    status: 1,
                    statusId: window.APP.display_project_id,
                    ...localQuery,
                };
                const response = await axios.get(
                    route("project-management.projects.index"),
                    {
                        params: query,
                    }
                );
                this.projects = response.data.data;
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        },
        async fetchAllEmployees(query) {
            let queryData = {
                perPage: "all",
                status: "active",
                ...(query ? query : {}),
            };
            await axios
                .get(route("employee-management.employees.index"), {
                    params: queryData,
                })
                .then((response) => {
                    this.allEmployees = response.data.data;
                })
                .catch((error) => {
                    console.error("Error fetching employees:", error);
                })
                .finally(() => { });
        },
    },
});
